import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Chip, CircularProgress, IconButton,
  Breadcrumbs, Link, Divider, Grid, Tooltip, Dialog, DialogContent, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useParams, useNavigate } from 'react-router-dom';
import { getDecisionTree } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import DecisionMedia from '../components/DecisionMedia';

const NODE_TYPE_LABEL = {
  question: 'Pregunta',
  info: 'Información',
  leaf: 'Resultado',
};

// Visor de galería tipo carrusel: recorre TODA la media pasada (la del nodo,
// o solo una imagen de opción) con flechas, teclado y contador — reemplaza
// el dialog simple que solo mostraba una imagen fija sin poder navegar.
const MediaGallery = ({ items, index, onClose, onNavigate }) => {
  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNavigate((i) => (i + 1) % items.length);
      if (e.key === 'ArrowLeft') onNavigate((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, items.length, onClose, onNavigate]);

  if (index === null) return null;
  const current = items[index];

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogContent sx={{ p: 0, bgcolor: 'black', position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: 'white', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}
        >
          <CloseIcon />
        </IconButton>

        {items.length > 1 && (
          <Typography sx={{ position: 'absolute', top: 12, left: 12, zIndex: 2, color: 'white', bgcolor: 'rgba(0,0,0,0.4)', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.8rem' }}>
            {index + 1} / {items.length}
          </Typography>
        )}

        {items.length > 1 && (
          <IconButton
            onClick={() => onNavigate((i) => (i - 1 + items.length) % items.length)}
            sx={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', zIndex: 2, color: 'white', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}
          >
            <NavigateBeforeIcon />
          </IconButton>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 320 }}>
          <DecisionMedia
            key={current.id}
            media={current}
            sx={{ maxWidth: '100%', maxHeight: '75vh', display: 'block', mx: 'auto' }}
          />
          {current.caption && (
            <Typography variant="body2" sx={{ color: 'grey.300', p: 2, textAlign: 'center' }}>
              {current.caption}
            </Typography>
          )}
        </Box>

        {items.length > 1 && (
          <IconButton
            onClick={() => onNavigate((i) => (i + 1) % items.length)}
            sx={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', zIndex: 2, color: 'white', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}
          >
            <NavigateNextIcon />
          </IconButton>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Cada opción es una tarjeta clickeable (elige la rama), y si tiene imagen
// de referencia agrega un "Ver más" que la abre en grande sin elegir todavía
// — la imagen ayuda a decidir CUÁL opción tomar, no es solo decoración.
const OptionCard = ({ option, onChoose, onViewImage }) => (
  <Paper
    variant="outlined"
    onClick={() => onChoose(option.to_node_id, option.label)}
    sx={{
      display: 'flex', alignItems: 'stretch', overflow: 'hidden', cursor: 'pointer',
      transition: 'border-color 0.15s, background-color 0.15s',
      '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
    }}
  >
    {option.image_media && (
      <Box
        onClick={(e) => { e.stopPropagation(); onViewImage(); }}
        sx={{ position: 'relative', width: 96, flexShrink: 0, cursor: 'zoom-in' }}
      >
        <DecisionMedia media={option.image_media} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <Box sx={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0)', transition: 'background-color 0.15s',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.35)' },
          '&:hover .zoom-icon': { opacity: 1 },
        }}>
          <ZoomInIcon className="zoom-icon" sx={{ color: 'white', opacity: 0, transition: 'opacity 0.15s' }} />
        </Box>
      </Box>
    )}
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1.5, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600 }}>{option.label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        {option.image_media && (
          <Button
            size="small"
            onClick={(e) => { e.stopPropagation(); onViewImage(); }}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Ver más
          </Button>
        )}
        {option.rationale_text && (
          <Tooltip title={option.rationale_text} onClick={(e) => e.stopPropagation()}>
            <HelpOutlineIcon fontSize="small" sx={{ color: 'text.disabled' }} />
          </Tooltip>
        )}
      </Box>
    </Box>
  </Paper>
);

const AlgoritmoViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isMedico } = useAuth();
  const canEdit = isAdmin || isMedico;

  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [path, setPath] = useState([]); // [{nodeId, optionLabel}] — para el breadcrumb

  // Galería activa: qué lista de items se está recorriendo (la media del
  // nodo, o la imagen de una sola opción) y en qué índice.
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryIndex, setGalleryIndex] = useState(null);

  useEffect(() => {
    setLoading(true);
    getDecisionTree(id)
      .then((r) => {
        setTree(r.data);
        setCurrentNodeId(r.data.root_node_id);
        setPath([]);
      })
      .catch((err) => setError(err.userMessage || 'No se pudo cargar el algoritmo'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (error || !tree) {
    return (
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Typography color="error">{error || 'Algoritmo no encontrado'}</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/algoritmos')}>Volver a Algoritmos</Button>
      </Box>
    );
  }
  if (!tree.root_node_id) {
    return (
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Typography color="text.secondary">Este algoritmo todavía no tiene un nodo inicial definido.</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/algoritmos')}>Volver a Algoritmos</Button>
      </Box>
    );
  }

  const currentNode = tree.nodes.find((n) => n.id === currentNodeId);

  const goToNode = (nodeId, optionLabel) => {
    setPath((p) => [...p, { nodeId: currentNodeId, optionLabel }]);
    setCurrentNodeId(nodeId);
  };

  const goToBreadcrumb = (index) => {
    // index === -1 vuelve a la raíz; index >= 0 vuelve al nodo destino de ese paso
    if (index < 0) {
      setCurrentNodeId(tree.root_node_id);
      setPath([]);
      return;
    }
    setCurrentNodeId(path[index + 1]?.nodeId ?? currentNodeId);
    setPath((p) => p.slice(0, index + 1));
  };

  // Reconstruir los títulos visitados para el breadcrumb: cada paso del path
  // guarda el nodo DESDE el que se salió; el título a mostrar es el nodo destino,
  // que es el nodo actual del paso siguiente (o el nodo actual si es el último).
  const breadcrumbNodes = path.map((step, i) => {
    const destNodeId = i + 1 < path.length ? path[i + 1].nodeId : currentNodeId;
    const destNode = tree.nodes.find((n) => n.id === destNodeId);
    return { label: destNode?.title || '…', optionLabel: step.optionLabel, index: i };
  });

  const restart = () => {
    setCurrentNodeId(tree.root_node_id);
    setPath([]);
  };

  const openGallery = (items, startIndex) => {
    setGalleryItems(items);
    setGalleryIndex(startIndex);
  };
  const closeGallery = () => { setGalleryIndex(null); setGalleryItems([]); };
  const navigateGallery = (updater) => setGalleryIndex((i) => updater(i));

  return (
    <Box sx={{ maxWidth: 780, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/algoritmos')} size="small"><ArrowBackIcon /></IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} noWrap>{tree.title}</Typography>
          {tree.specialty_tag && (
            <Typography variant="caption" color="text.secondary">{tree.specialty_tag}</Typography>
          )}
        </Box>
        {canEdit && (
          <Tooltip title="Editar este algoritmo">
            <IconButton size="small" onClick={() => navigate(`/algoritmos/${id}/editar`)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Volver al inicio del algoritmo">
          <IconButton size="small" onClick={restart} disabled={path.length === 0}>
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {path.length > 0 && (
        <Breadcrumbs sx={{ mb: 2, fontSize: '0.85rem' }}>
          <Link component="button" underline="hover" onClick={() => goToBreadcrumb(-1)} sx={{ fontSize: '0.85rem' }}>
            Inicio
          </Link>
          {breadcrumbNodes.map((b, i) => (
            i === breadcrumbNodes.length - 1 ? (
              <Typography key={i} color="text.primary" sx={{ fontSize: '0.85rem' }}>{b.label}</Typography>
            ) : (
              <Link key={i} component="button" underline="hover" onClick={() => goToBreadcrumb(i)} sx={{ fontSize: '0.85rem' }}>
                {b.label}
              </Link>
            )
          ))}
        </Breadcrumbs>
      )}

      {!currentNode ? (
        <Alert severity="warning">Este nodo ya no existe en el algoritmo.</Alert>
      ) : (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Chip
            label={NODE_TYPE_LABEL[currentNode.node_type] || currentNode.node_type}
            size="small"
            color={currentNode.node_type === 'leaf' ? 'success' : 'default'}
            icon={currentNode.node_type === 'leaf' ? <CheckCircleIcon /> : undefined}
            sx={{ mb: 1.5 }}
          />
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5 }}>
            {currentNode.title}
          </Typography>
          {currentNode.body_text && (
            <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {currentNode.body_text}
            </Typography>
          )}

          {currentNode.media.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={1.5}>
                {currentNode.media.map((m, i) => (
                  <Grid size={{ xs: 6, sm: 4 }} key={m.id}>
                    <Box
                      onClick={() => openGallery(currentNode.media, i)}
                      sx={{ cursor: 'pointer', position: 'relative' }}
                    >
                      <DecisionMedia
                        media={m}
                        sx={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 1.5 }}
                      />
                      {m.caption && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {m.caption}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
              {currentNode.media.length > 1 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Tocá cualquier imagen para recorrerlas en pantalla completa
                </Typography>
              )}
            </Box>
          )}

          {currentNode.options.length > 0 && (
            <>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {currentNode.options.map((o) => (
                  <OptionCard
                    key={o.id}
                    option={o}
                    onChoose={goToNode}
                    onViewImage={() => openGallery([o.image_media], 0)}
                  />
                ))}
              </Box>
            </>
          )}

          {currentNode.node_type === 'leaf' && (
            <Button sx={{ mt: 2 }} startIcon={<RestartAltIcon />} onClick={restart}>
              Empezar de nuevo
            </Button>
          )}
        </Paper>
      )}

      <MediaGallery
        items={galleryItems}
        index={galleryIndex}
        onClose={closeGallery}
        onNavigate={navigateGallery}
      />
    </Box>
  );
};

export default AlgoritmoViewer;
